import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';
import { Product } from '../types';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';
import { normalizeProductSizes, adjustSizeStock } from './inventoryService';

const COLLECTION_NAME = 'products';

/**
 * Normalizes product document data to ensure the retail multi-size schema is strictly adhered to.
 * Sizes array is the ONLY source of truth for stock.
 */
export function normalizeProduct(raw: any): Product {
  const sizes = normalizeProductSizes(raw.sizes, raw.size, raw.quantity);
  const primarySize = sizes.find((s) => s.stock > 0)?.size || sizes[0]?.size || 'Standard';

  // Extract and omit any obsolete raw.quantity property
  const { quantity: _obsoleteQty, ...cleanRaw } = raw;

  return {
    ...cleanRaw,
    sizes,
    size: primarySize,
    images: Array.isArray(cleanRaw.images) && cleanRaw.images.length > 0
      ? cleanRaw.images
      : ['https://images.unsplash.com/photo-1591047139829-d91aecb6caea?auto=format&fit=crop&w=800&q=80']
  };
}

export function subscribeProducts(
  onData: (products: Product[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const productsRef = collection(db, COLLECTION_NAME);
  return onSnapshot(
    productsRef,
    (snapshot) => {
      const products: Product[] = snapshot.docs.map((docSnap) =>
        normalizeProduct({
          id: docSnap.id,
          ...docSnap.data()
        })
      );
      onData(products);
    },
    (err) => {
      console.error('Error listening to products collection:', err);
      if (onError) onError(err);
    }
  );
}

export async function addProduct(product: Omit<Product, 'id' | 'createdAt'>): Promise<string> {
  const newRef = doc(collection(db, COLLECTION_NAME));
  const normalized = normalizeProduct(product);
  const newProduct: Product = {
    ...normalized,
    id: newRef.id,
    createdAt: new Date().toISOString()
  };
  await setDoc(newRef, sanitizeFirestoreData(newProduct));
  return newRef.id;
}

export async function updateProduct(id: string, updates: Partial<Product>): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  
  // Normalize sizes if updated
  if (updates.sizes) {
    updates.sizes = normalizeProductSizes(updates.sizes, updates.size);
    if (!updates.size) {
      updates.size = updates.sizes[0]?.size || 'Standard';
    }
  }

  // Ensure no legacy quantity field is written to Firestore
  const { quantity: _obsoleteQty, ...sanitizedUpdates } = updates as any;
  await updateDoc(docRef, sanitizeFirestoreData(sanitizedUpdates));
}

export async function deleteProduct(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

/**
 * Adjust stock for a specific size in a product.
 * Executes atomically within a Firestore transaction via inventoryService.
 */
export async function adjustProductSizeStock(product: Product, targetSize: string, delta: number): Promise<void> {
  await adjustSizeStock(product.id, targetSize, delta);
}
